/**
 * Authentication Store (Vue 3 Composable)
 * Provides reactive authentication state management
 * 
 * @description This composable manages authentication state and provides
 * reactive access to user data, authentication status, and auth methods
 */

import { ref, computed, watch } from 'vue';
import authService from '@/services/authService';
import { useToast } from 'vue-toastification';

// Global reactive state
const isAuthenticated = ref(false);
const currentUser = ref(null);
const isLoading = ref(false);
const authError = ref(null);

export function useAuth() {
  const toast = useToast();

  // Initialize state from auth service
  const initializeAuth = () => {
    isAuthenticated.value = authService.isLoggedIn();
    currentUser.value = authService.getCurrentUser();
  };

  // Login function
  const login = async (credentials) => {
    isLoading.value = true;
    authError.value = null;

    try {
      const result = await authService.login(credentials.email, credentials.password);
      
      if (result.success) {
        isAuthenticated.value = true;
        currentUser.value = authService.getCurrentUser();
        // Don't show success toast on login - the redirect is enough feedback
        // and prevents the toast from being cut off by navigation
        return { success: true };
      } else {
        authError.value = result.message;
        toast.error(result.message || 'Login failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      const message = error.message || 'Login failed';
      authError.value = message;
      toast.error(message);
      return { success: false, message };
    } finally {
      isLoading.value = false;
    }
  };

  // Register function
  const register = async (userData) => {
    isLoading.value = true;
    authError.value = null;

    try {
      const result = await authService.register(userData);
      
      if (result.success) {
        // If auto-login occurred, update the reactive state
        if (result.autoLogin) {
          isAuthenticated.value = true;
          currentUser.value = authService.getCurrentUser();
        }
        
        toast.success(result.message || 'Registration successful');
        return { success: true, autoLogin: result.autoLogin };
      } else {
        authError.value = result.message;
        toast.error(result.message || 'Registration failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      const message = error.message || 'Registration failed';
      authError.value = message;
      toast.error(message);
      return { success: false, message };
    } finally {
      isLoading.value = false;
    }
  };

  // Logout function
  const logout = async () => {
    isLoading.value = true;
    
    try {
      await authService.logout();
      isAuthenticated.value = false;
      currentUser.value = null;
      authError.value = null;
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout on client side even if server call fails
      isAuthenticated.value = false;
      currentUser.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  // Check if user has required role
  const hasRole = (roles) => {
    return authService.hasRole(roles);
  };

  // Get authorization header for API calls
  const getAuthHeader = () => {
    return authService.getAuthHeader();
  };

  // Computed properties
  const userDisplayName = computed(() => {
    return currentUser.value?.name || currentUser.value?.email || 'User';
  });

  const userRole = computed(() => {
    return currentUser.value?.role || 'user';
  });

  const isAdmin = computed(() => {
    return userRole.value === 'admin';
  });

  // Watch for auth service changes
  watch(
    () => authService.isLoggedIn(),
    (newValue) => {
      if (newValue !== isAuthenticated.value) {
        isAuthenticated.value = newValue;
        currentUser.value = authService.getCurrentUser();
      }
    }
  );

  // Check authentication status (validates token and refreshes user data)
  const checkAuthStatus = async () => {
    try {
      // Check if we have a stored token
      const token = authService.getToken();
      if (!token) {
        isAuthenticated.value = false;
        currentUser.value = null;
        return false;
      }

      // Verify token is still valid
      if (authService.isLoggedIn()) {
        isAuthenticated.value = true;
        currentUser.value = authService.getCurrentUser();
        return true;
      } else {
        // Token expired or invalid
        isAuthenticated.value = false;
        currentUser.value = null;
        return false;
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      isAuthenticated.value = false;
      currentUser.value = null;
      return false;
    }
  };

  // Initialize on first use
  if (!isAuthenticated.value && authService.isLoggedIn()) {
    initializeAuth();
  }

  return {
    // State
    isAuthenticated: computed(() => isAuthenticated.value),
    currentUser: computed(() => currentUser.value),
    isLoading: computed(() => isLoading.value),
    authError: computed(() => authError.value),
    userDisplayName,
    userRole,
    isAdmin,

    // Methods
    login,
    register,
    logout,
    checkAuthStatus,
    hasRole,
    getAuthHeader,
    initializeAuth
  };
}
